<template>
  <Teleport v-if="yuvaHazir" to="#sparqon-bar-yuvasi">
    <button type="button" class="ss-dugme" :aria-expanded="acik" aria-label="Sparqon asistanı" @click="acik = !acik">
      <i class="pi pi-sparkles"></i>
      <span class="hidden lg:inline">Sparqon</span>
    </button>
  </Teleport>

  <Drawer v-model:visible="acik" position="right" class="!w-full sm:!w-[30rem]" :pt="{ content: { class: '!p-0 flex flex-col' } }">
    <template #header>
      <div class="flex items-center gap-2 min-w-0">
        <i class="pi pi-sparkles renk-uyari"></i>
        <div class="min-w-0">
          <div class="font-semibold text-gray-900 dark:text-gray-100">Sparqon · Konsol</div>
          <div class="text-xs text-gray-500 truncate">Kiracı, lisans, gelir, görev ve çağrı soruları</div>
        </div>
      </div>
    </template>

    <div ref="liste" class="flex-1 overflow-y-auto p-3 flex flex-col gap-3" aria-live="polite">
      <div v-if="!mesajlar.length" class="flex flex-col gap-2">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Konsol verileri hakkında sorun ya da duyuru, lisans uzatma, kota tanımlama gibi işleri isteyin — her yazma işlemi önce size gösterilir, onayınızla yapılır.
        </p>
        <div class="text-xs text-gray-500 mt-1">Örnekler</div>
        <button v-for="o in ornekler" :key="o" type="button" class="ss-ornek" @click="gonder(o)">{{ o }}</button>
      </div>

      <div v-for="(m, i) in mesajlar" :key="i" class="flex" :class="m.rol === 'user' ? 'justify-end' : 'justify-start'">
        <div v-if="m.rol === 'user'" class="ss-balon ss-kullanici">{{ m.metin }}</div>
        <div v-else class="ss-balon ss-asistan" :class="{ 'ss-uyari': m.uyari }">
          <div class="ss-metin" v-html="bicimle(m.metin)"></div>
          <div v-if="m.adimlar && m.adimlar.length" class="flex flex-wrap gap-1 mt-2">
            <span v-for="(a, j) in m.adimlar" :key="j" class="ss-adim" :title="a.aciklama">{{ adimAd(a.id) }}</span>
          </div>
          <Button v-if="m.rota" class="mt-2" icon="pi pi-arrow-right" label="Sayfaya git" @click="rotayaGit(m.rota)" />
          <div v-if="m.onay" class="ss-onay mt-2">
            <dl v-if="m.onay.detay" class="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs mb-2">
              <template v-for="(v, k) in duzDetay(m.onay.detay)" :key="k">
                <dt class="text-gray-500">{{ k }}</dt>
                <dd class="text-gray-800 dark:text-gray-100 break-words whitespace-pre-line">{{ v }}</dd>
              </template>
            </dl>
            <div v-if="m.onayDurum === 'bekliyor'" class="flex gap-2">
              <Button icon="pi pi-check" label="Onayla" :disabled="bekliyor" @click="onayla(m)" />
              <Button text severity="secondary" label="Vazgeç" :disabled="bekliyor" @click="vazgec(m)" />
            </div>
            <div v-else class="text-xs text-gray-500">{{ m.onayDurum === 'onaylandi' ? 'Onaylandı' : 'Vazgeçildi' }}</div>
          </div>
        </div>
      </div>

      <div v-if="bekliyor" class="flex justify-start">
        <div class="ss-balon ss-asistan text-gray-500 flex items-center gap-2"><i class="pi pi-spin pi-spinner"></i> Düşünüyor…</div>
      </div>
    </div>

    <form class="p-3 border-t border-gray-200 dark:border-gray-800 flex flex-col gap-2" @submit.prevent="gonder()">
      <div class="flex gap-2 items-end">
        <label for="ss-girdi" class="sr-only">Sparqon'a soru</label>
        <Textarea id="ss-girdi" ref="girdi" v-model="girdi" rows="1" autoResize class="flex-1 max-h-40" placeholder="Bir soru yazın…" :disabled="bekliyor"
          @keydown.enter.exact.prevent="gonder()" />
        <Button type="submit" icon="pi pi-send" :disabled="bekliyor || !girdi.trim()" aria-label="Gönder" />
      </div>
      <div class="flex justify-between text-[11px] text-gray-400">
        <span>Yalnız konsol yetkinizdeki bölümler yanıtlanır.</span>
        <button v-if="mesajlar.length" type="button" class="hover:underline" @click="temizle">Sohbeti temizle</button>
      </div>
    </form>
  </Drawer>
</template>

<script>
// SK3-S (KONSOL-V3 K4/V5): sağlayıcı Sparqon'u — AiSparqonSistemSor. Kiracı AIMessenger'ından ayrı:
// o bileşen kiracı uçlarına (bildirim, brifing, içgörü, kural) bağlı ve sohbetini DB'den okuyor;
// konsol sohbeti sessionStorage'da tutulur (BE log satırları SohbetteGizli=1). Yazma işlemleri
// önizleme → Onayla (onay kodu + önizleme argümanı ayrı Onay alanında gider, model görmez) → BE'de HMAC doğrulama.
const ANAHTAR = "sistemSparqonSohbet";
const ADIM_AD = {
  sistem_sirket_ara: "şirket arama", sistem_sirket_ozet: "şirket özeti", sistem_lisans_durum: "lisans durumu",
  sistem_siparis_listesi: "siparişler", sistem_gelir_ozet: "gelir", sistem_job_sagligi: "görev sağlığı",
  sistem_hata_ozet: "hata kaydı", sistem_acik_cagrilar: "açık çağrılar", sistem_surum_son: "sürümler",
  sistem_kota_durum: "WhatsApp kotası", sistem_duyuru_listesi: "duyurular", sistem_duyuru_gonder: "duyuru",
  sistem_siparis_aktif_et: "sipariş aktivasyonu", sistem_lisans_uzat: "lisans uzatma", sistem_kota_tanimla: "kota tanımlama",
  sistem_sirket_not: "şirket notu",
};

export default {
  name: "SistemSparqon",
  data() {
    let mesajlar = [];
    try { mesajlar = JSON.parse(sessionStorage.getItem(ANAHTAR) || "[]") || []; } catch (e) { mesajlar = []; }
    return {
      yuvaHazir: false,
      acik: false,
      girdi: "",
      bekliyor: false,
      mesajlar,
      ornekler: [
        "Lisansı 30 gün içinde bitecek şirketler hangileri?",
        "Görevlerde son 24 saatte hata var mı?",
        "Bu ayki abonelik geliri ne kadar, geçen aya göre durum ne?",
        "Açık sistem çağrılarını özetle",
      ],
    };
  },
  mounted() {
    this.$nextTick(() => { this.yuvaHazir = !!document.getElementById("sparqon-bar-yuvasi"); });
  },
  watch: {
    acik(v) {
      if (v) this.$nextTick(() => { this.asagi(); this.odaklan(); });
    },
  },
  methods: {
    kaydet() {
      try { sessionStorage.setItem(ANAHTAR, JSON.stringify(this.mesajlar.slice(-40))); } catch (e) { /* yok say */ }
    },
    asagi() {
      const l = this.$refs.liste;
      if (l) l.scrollTop = l.scrollHeight;
    },
    odaklan() {
      const g = this.$refs.girdi && this.$refs.girdi.$el;
      if (g && g.focus) g.focus();
    },
    gecmis() {
      return this.mesajlar.filter((m) => m.metin).slice(-12).map((m) => ({ Rol: m.rol, Metin: m.metin }));
    },
    gonder(metin, onay) {
      const soru = (metin != null ? metin : this.girdi).trim();
      if (!soru || this.bekliyor) return;
      const gecmis = this.gecmis();
      this.mesajlar.push({ rol: "user", metin: soru });
      this.girdi = "";
      this.bekliyor = true;
      this.$nextTick(this.asagi);
      const govde = { Mesaj: soru, Gecmis: gecmis };
      if (onay) govde.Onay = onay;
      this.$Servis("POST", "AiSparqonSistemSor", govde)
        .then((res) => {
          if (res.status === 200 && res.data) {
            const d = res.data;
            this.mesajlar.push({
              rol: "assistant",
              metin: d.cevap || "",
              rota: d.rota || null,
              adimlar: d.adimlar || [],
              onay: d.onay || null,
              onayDurum: d.onay ? "bekliyor" : null,
            });
            if (d.islem && d.islem.basarili) this.$toast.add({ severity: "success", summary: "Sparqon", detail: d.cevap, life: 4000 });
          } else if (res.status === 999) {
            this.mesajlar.push({ rol: "assistant", metin: String(res.data || "Bu isteği yanıtlayamıyorum."), uyari: true });
          } else {
            this.mesajlar.push({ rol: "assistant", metin: "Bir hata oluştu, biraz sonra tekrar deneyin.", uyari: true });
          }
        })
        .catch(() => { this.mesajlar.push({ rol: "assistant", metin: "Sunucuya ulaşılamadı.", uyari: true }); })
        .finally(() => {
          this.bekliyor = false;
          this.kaydet();
          this.$nextTick(() => { this.asagi(); this.odaklan(); });
        });
    },
    onayla(m) {
      if (this.bekliyor || !m.onay) return;
      m.onayDurum = "onaylandi";
      const onay = { Aksiyon: m.onay.aksiyon, Arg: m.onay.arg || {}, OnayKodu: m.onay.onayKodu };
      // Kod yalnız bu istekte gider; sohbet geçmişine/oturum deposuna kalmaz.
      m.onay = { detay: m.onay.detay };
      this.gonder("Onaylıyorum.", onay);
    },
    vazgec(m) {
      m.onayDurum = "vazgecildi";
      if (m.onay) m.onay = { detay: m.onay.detay };
      this.mesajlar.push({ rol: "assistant", metin: "Tamam, işlemi yapmadım." });
      this.kaydet();
    },
    temizle() {
      this.mesajlar = [];
      this.kaydet();
    },
    rotayaGit(r) {
      this.acik = false;
      this.$router.push(r);
    },
    adimAd(id) {
      return ADIM_AD[id] || id;
    },
    duzDetay(d) {
      const cikti = {};
      Object.keys(d || {}).forEach((k) => {
        const v = d[k];
        if (v == null || v === "") return;
        if (Array.isArray(v)) cikti[k] = v.map((x) => (typeof x === "object" ? Object.values(x).join(" × ") : x)).join("\n");
        else if (typeof v === "object") cikti[k] = JSON.stringify(v);
        else cikti[k] = String(v);
      });
      return cikti;
    },
    // Önce HTML kaçışı, sonra yalnız **kalın**, satır sonu ve "- " maddeleri — model çıktısı asla HTML olarak işlenmez.
    bicimle(t) {
      const kacis = String(t || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      return kacis
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .split("\n")
        .map((s) => (s.startsWith("- ") ? "<span class=\"ss-madde\">• " + s.slice(2) + "</span>" : s))
        .join("<br>");
    },
  },
};
</script>

<style scoped>
.ss-dugme {
  display: inline-flex; align-items: center; gap: 0.4rem; height: 2.25rem; padding: 0 0.7rem;
  border-radius: 0.5rem; font-size: 0.875rem; font-weight: 500; color: #7c2d12;
  background: linear-gradient(135deg, #ffedd5, #fef3c7);
}
.ss-dugme:hover { filter: brightness(0.97); }
.ss-dugme:focus-visible { outline: 2px solid #ed7a18; outline-offset: 2px; }
.ss-ornek {
  text-align: left; font-size: 0.85rem; padding: 0.5rem 0.7rem; border-radius: 0.6rem;
  border: 1px solid #e5e7eb; color: #374151;
}
.ss-ornek:hover { border-color: #fdba74; background: #fff7ed; }
.ss-balon { max-width: 88%; padding: 0.55rem 0.75rem; border-radius: 0.8rem; font-size: 0.875rem; line-height: 1.45; }
.ss-kullanici { background: #ed7a18; color: #fff; border-bottom-right-radius: 0.25rem; white-space: pre-wrap; }
.ss-asistan { background: #f3f4f6; color: #111827; border-bottom-left-radius: 0.25rem; }
.ss-uyari { background: #fef3c7; color: #78350f; }
.ss-adim { font-size: 0.68rem; padding: 0.05rem 0.4rem; border-radius: 999px; background: #e5e7eb; color: #4b5563; }
.ss-onay { border-top: 1px dashed #d1d5db; padding-top: 0.5rem; }
:deep(.ss-madde) { display: inline-block; padding-left: 0.2rem; }

.dark .ss-dugme, .my-app-dark .ss-dugme { background: rgba(237, 122, 24, 0.18); color: #fdba74; }
.dark .ss-ornek, .my-app-dark .ss-ornek { border-color: #374151; color: #d1d5db; }
.dark .ss-ornek:hover, .my-app-dark .ss-ornek:hover { background: rgba(237, 122, 24, 0.1); }
.dark .ss-asistan, .my-app-dark .ss-asistan { background: #1f2937; color: #e5e7eb; }
.dark .ss-uyari, .my-app-dark .ss-uyari { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
.dark .ss-adim, .my-app-dark .ss-adim { background: #374151; color: #cbd5e1; }
.dark .ss-onay, .my-app-dark .ss-onay { border-color: #374151; }
</style>
